/**
 * Formats a timestamp string as a compact relative time (e.g., "2m ago", "3h ago")
 * Returns "Just now" for invalid/missing timestamps
 */
export function formatTimestampCompact(timestamp?: string): string {
  if (!timestamp) return "Just now";

  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "Just now";

  const diffMins = Math.floor((Date.now() - date.getTime()) / 60000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  return date.toLocaleDateString();
}

/**
 * Formats a timestamp for use in chat dividers (iMessage-style)
 * Shows time for today, day + time for this week, full date otherwise
 */
export function formatTimestampDivider(timestamp?: string): string {
  if (!timestamp) return "";

  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "";

  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  const isThisWeek = diffDays < 7;

  const timeStr = date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  if (isToday) {
    return timeStr;
  } else if (isYesterday) {
    return `Yesterday ${timeStr}`;
  } else if (isThisWeek) {
    const dayName = date.toLocaleDateString([], { weekday: "long" });
    return `${dayName} ${timeStr}`;
  } else {
    return date.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
  }
}

/**
 * Checks if there's a significant time gap between two timestamps
 * @param timestamp1 Earlier timestamp
 * @param timestamp2 Later timestamp
 * @param thresholdMinutes Minimum gap in minutes (default: 5)
 */
export function hasSignificantTimeGap(
  timestamp1?: string,
  timestamp2?: string,
  thresholdMinutes: number = 5
): boolean {
  if (!timestamp1 || !timestamp2) return false;

  const date1 = new Date(timestamp1);
  const date2 = new Date(timestamp2);

  if (isNaN(date1.getTime()) || isNaN(date2.getTime())) return false;

  const diffMs = Math.abs(date2.getTime() - date1.getTime());
  const diffMins = diffMs / (1000 * 60);

  return diffMins >= thresholdMinutes;
}

/**
 * Formats a date relative to the current time
 * @param date The date to format
 * @returns A string representing the relative time (e.g., "2 hours ago", "Yesterday", etc.)
 */
export function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  // Handle future dates gracefully
  if (diffMs < 0) {
    return "just now";
  }

  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  // Format times
  if (diffSecs < 60) {
    return "just now";
  } else if (diffMins < 60) {
    return `${diffMins} ${diffMins === 1 ? "minute" : "minutes"} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
  } else if (diffDays === 1) {
    return "Yesterday";
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else if (diffWeeks < 4) {
    return `${diffWeeks} ${diffWeeks === 1 ? "week" : "weeks"} ago`;
  } else if (diffMonths < 12) {
    return `${diffMonths} ${diffMonths === 1 ? "month" : "months"} ago`;
  } else {
    // For anything older than a year, use the standard date format
    return date.toLocaleDateString();
  }
}
